import { Grid, Stack } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ISaveWorkspacePaidButNotOnboardedParam,
  saveWorkspacePaidButNotOnboarded,
} from 'wisestep-api';
import {
  Label,
  WsInputNumber,
  WsSelect,
  WsColors,
  WsButton,
  ControlledTextInput,
  WsDatePicker,
  WsTitleBanner,
  WsRadioGroup,
  WsRadioObj,
} from 'wisestep-base';

import { getCurrencies } from '../../../../utils/CurrencyHelper';
import { success, failed } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';

export interface IPaidButNotOnboardedCustomersForm {
  usersData?: ISaveWorkspacePaidButNotOnboardedParam;
}

const PaidButNotOnboardedCustomersForm = ({ usersData }: IPaidButNotOnboardedCustomersForm) => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const navigate = useNavigate();
  const location = useLocation();
  const [workspacePaidUserData, setWorkspacePaidUserData] =
    useState<ISaveWorkspacePaidButNotOnboardedParam>();

  const workspaceDetailsDefaultValues: ISaveWorkspacePaidButNotOnboardedParam = {
    companyName: '',
    numberOfUsersPaid: 0,
    currency: '',
    amount: 0,
    paidDate: null,
    onboardedStatus: false,
    workspaceName: '',
    isOnboarded: undefined,
    onboardedDate: null,
  };

  const formOptions = {
    defaultValues: usersData
      ? {
          ...usersData,
          paidDate: new Date(usersData.paidDate ?? '') ?? null,
          onboardedDate: new Date(usersData.onboardedDate ?? '') ?? null,
        }
      : workspaceDetailsDefaultValues,
  };

  const methods = useForm<ISaveWorkspacePaidButNotOnboardedParam>(formOptions);
  const { watch, handleSubmit } = methods;

  const currencies = getCurrencies().map((val) => {
    return { label: val, value: val };
  });

  const onboardedStatusOptions: WsRadioObj[] = [
    {
      label: 'Yes',
      value: 'yes',
    },
    {
      label: 'No',
      value: 'no',
    },
  ];

  const onboardedStatusSelect = watch('isOnboarded');

  const onFormSubmit = async (data: ISaveWorkspacePaidButNotOnboardedParam) => {
    try {
      const response = await saveWorkspacePaidButNotOnboarded({
        ...data,
        onboardedStatus: Boolean(data.isOnboarded == 'yes'),
      });
      setWorkspacePaidUserData(response.data);
      setNotification(success('Saved workspace users successfully!'));
      location.pathname === '/business-metrics/onbording-details/get'
        ? window.location.reload()
        : navigate('/business-metrics/onbording-details/get');
    } catch (e) {
      setNotification(failed('Some error occured. Please try later!'));
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        {isEmpty(usersData) && (
          <WsTitleBanner title="Paid But Not Yet Onboarded" backButton={false} />
        )}
        <Grid pt={2}>
          <Grid
            bgcolor={WsColors.White}
            p={2}
            item
            xs={12}
            alignItems="center"
            justifyContent="center"
            container
          >
            <Grid item xs={6.1}>
              <Label>Company Name</Label>
              <ControlledTextInput
                style={{ marginTop: '12px' }}
                name="companyName"
                labelText=""
                type="text"
                fullWidth
                id="companyId"
                placeholder="Enter Company Name"
                variant="outlined"
                autoComplete="companyName"
              />
            </Grid>
            <Grid item xs={6.1}>
              <Label>Number of Users Paid for</Label>
              <ControlledTextInput
                name="numberOfUsersPaid"
                labelText=""
                type="number"
                fullWidth
                id="numberOfUsersPaid"
                placeholder=""
                variant="outlined"
                autoComplete="numberOfUsersPaid"
              />
            </Grid>
            <Grid item xs={6.1}>
              <Stack direction="row" spacing={2}>
                <WsSelect
                  name="currency"
                  label="Select Currency"
                  selectArray={currencies}
                  variant="outlined"
                />
                <WsInputNumber name="amount" label="Amount" variant="outlined" />
              </Stack>
            </Grid>
            <Grid item xs={6.1}>
              <Label>Paid Date</Label>
              <WsDatePicker name="paidDate" label="" disableFuture={true} />
            </Grid>
            <Grid item xs={6.1}>
              <Label>Onboarded Status </Label>
              <WsRadioGroup name="isOnboarded" selectArray={onboardedStatusOptions} />
            </Grid>
            {onboardedStatusSelect == 'yes' && (
              <>
                <Grid item xs={6.1} container direction="row">
                  <Label>Onboarded Date</Label>
                  <WsDatePicker
                    name="onboardedDate"
                    label=""
                    disableFuture={true}
                    required={false}
                  />
                </Grid>
                <Grid item xs={6.1}>
                  <ControlledTextInput
                    name="workspaceName"
                    labelText="Workspace Name"
                    id="workspace_name"
                    placeholder={workspacePaidUserData?.workspaceName ?? ''}
                    variant="outlined"
                    autoComplete="workspace_name"
                    fullWidth
                  />
                </Grid>
              </>
            )}
            <Grid item xs={6.1} justifyContent="flex-end" pt={2}>
              <WsButton size="large" type="submit" disabled={onboardedStatusSelect == undefined}>
                Save
              </WsButton>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};

export default PaidButNotOnboardedCustomersForm;
